import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import { MAX_DAILY_TREND_BUCKETS } from '@/domains/pages/shared/trend-query';

export type SpendGroupKind = 'task' | 'model' | 'team' | 'market' | 'tenure';

export const SPEND_GROUP_OPTIONS: { value: SpendGroupKind; label: string }[] = [
  { value: 'task', label: 'Task' },
  { value: 'model', label: 'Model' },
  { value: 'team', label: 'Team' },
  { value: 'market', label: 'Market' },
  { value: 'tenure', label: 'Tenure' },
];

/** Segment view group-by options omit the dimension the view is already scoped by. */
export const SEGMENT_SPEND_GROUP_OPTIONS: { value: SpendGroupKind; label: string }[] = [
  { value: 'task', label: 'Task' },
  { value: 'model', label: 'Model' },
];

export const DISTRIBUTION_STRIP_COVERAGE = 0.9;

export const SPEND_DISTRIBUTION_PAGE_SIZE = 10;

export const SPEND_DISTRIBUTION_TABLE_HEADER_HEIGHT_PX = 48;
export const SPEND_DISTRIBUTION_TABLE_ROW_HEIGHT_PX = 45;
export const SPEND_DISTRIBUTION_TABLE_FOOTER_HEIGHT_PX = 56;

export const SPEND_DISTRIBUTION_TABLE_LOADING_HEIGHT_PX =
  SPEND_DISTRIBUTION_TABLE_HEADER_HEIGHT_PX +
  SPEND_DISTRIBUTION_PAGE_SIZE * SPEND_DISTRIBUTION_TABLE_ROW_HEIGHT_PX +
  SPEND_DISTRIBUTION_TABLE_FOOTER_HEIGHT_PX;

export const SPEND_DISTRIBUTION_COMPOSITION_LOADING_HEIGHT_PX = 13 + 12 + 16;

export const SPEND_DISTRIBUTION_CARD_LOADING_BODY_HEIGHT_PX =
  SPEND_DISTRIBUTION_COMPOSITION_LOADING_HEIGHT_PX + 16 + SPEND_DISTRIBUTION_TABLE_LOADING_HEIGHT_PX;

export const SPEND_DISTRIBUTION_SECTION_LOADING_HEIGHT_PX = 32 + 16 + SPEND_DISTRIBUTION_CARD_LOADING_BODY_HEIGHT_PX;

export const SPEND_KPI_VALUE_ROW_HEIGHT_PX = 44;

export const SPEND_DISTRIBUTION_CARD_TITLE = 'AI spend distribution';

export const SPEND_DISTRIBUTION_CARD_TOOLTIP =
  'AI spend for the selected period, broken down by the dimension you group by.';

export const SPEND_SHARE_CHIP_TOOLTIP = 'Share of total AI spend for the selected period.';

export const SPEND_GROUP_COLUMN_LABEL: Record<SpendGroupKind, string> = {
  task: 'Task',
  model: 'Model',
  team: 'Team',
  market: 'Market',
  tenure: 'Tenure',
};

export const SPEND_GROUP_COLUMN_TOOLTIP: Record<SpendGroupKind, string> = {
  task: 'The type of work users completed with AI, grouped automatically from their interactions.',
  model: 'The LLM model users used to complete tasks, as reported by your integrations.',
  team: 'The team each user who completed these tasks belongs to.',
  market: 'The market each user who completed these tasks belongs to.',
  tenure: 'How long each user who completed these tasks has been with the company.',
};

/** Column help text for the distribution table, keyed by the column's sort key. */
export const SPEND_DISTRIBUTION_COLUMN_TOOLTIP = {
  cost: 'Total AI spend for the selected period, including its share of total AI spend.',
  spendPerTask: 'Total AI spend divided by the number of completed tasks.',
  tasks: 'Number of tasks users completed with AI during the selected period.',
  users: 'Number of distinct users who completed these tasks with AI.',
  failureRate: 'Share of attempted tasks excluded by your configured failure conditions.',
} as const;

export const SPEND_GROUP_OTHER_LABEL: Record<SpendGroupKind, string> = {
  task: 'Other tasks',
  model: 'Other models',
  team: 'Other teams',
  market: 'Other markets',
  tenure: 'Other tenures',
};

export const SPEND_GROUP_EMPTY_TITLE: Record<SpendGroupKind, string> = {
  task: 'No tasks computed yet',
  model: 'No models computed yet',
  team: 'No teams computed yet',
  market: 'No markets computed yet',
  tenure: 'No tenures computed yet',
};

export const COMPOSITION_COLLAPSE_BELOW_SHARE = 0.01;

/** Hide the unclassified remainder slice when it is below this share of total spend. */
export const COMPOSITION_UNCLASSIFIED_REMAINDER_MIN_SHARE = 0.005;

export const COMPOSITION_UNCLASSIFIED_SEGMENT_KEY = '__unclassified__';

export const COMPOSITION_UNCLASSIFIED_LABEL = 'Pending classification';

/** Muted gray distinct from the Other tail color (#71717A). */
export const COMPOSITION_UNCLASSIFIED_COLOR = '#A1A1AA';

export const COMPOSITION_MIN_SEGMENT_WIDTH_PX = 2;

export const TOTAL_AI_SPEND_CARD_TOOLTIP = 'Total AI spend across your organization for the selected period.';

export const AVG_SPEND_PER_TASK_CARD_TOOLTIP =
  'Average AI spend per task across your organization, broken down by team.';

/** Reads under the KPI figure, matching the AI Proficiency score description. */
export const AVG_SPEND_PER_TASK_DESCRIPTION =
  'Average cost per completed task across all AI models and providers used by users.';

/** Range-bar legend: the white tick is the org-wide average, the red tick the priciest segment. */
export const SPEND_RANGE_ORG_AVERAGE_LABEL = 'Org. avg';

export const SPEND_RANGE_ORG_AVERAGE_TOOLTIP =
  'Average AI spend per task across the organization for the selected period.';

/** Red tick pinned to the range maximum, with its brighter hover shade. */
export const SPEND_RANGE_MAX_TICK_COLOR = MULTIPLE_COLORS[2]!;
export const SPEND_RANGE_MAX_TICK_COLOR_LIT = '#F49B98';

/** Reference lines on the team bars. */
export const TEAM_COST_PER_TASK_ORG_AVERAGE_TOOLTIP =
  'Average AI spend per task across your organization. Teams above this line have higher AI spend per task than the organization average.';

export const TEAM_COST_PER_TASK_TOP_QUARTILE_TOOLTIP =
  'The top quartile includes the 25% of teams with the lowest AI spend per task in your organization.';

export const COMPOSITION_BAR_HEIGHT_CLASS = 'h-[13px]';

export const TEAM_COST_PER_TASK_BAR_HEIGHT_PX = 90;

/** Space above the bar plot for reference-line labels (text-[10px] + info icon). */
export const TEAM_COST_PER_TASK_REFERENCE_LABEL_HEIGHT_PX = 14;

export const TEAM_COST_PER_TASK_BAR_PLOT_HEIGHT_PX =
  TEAM_COST_PER_TASK_BAR_HEIGHT_PX - TEAM_COST_PER_TASK_REFERENCE_LABEL_HEIGHT_PX;

/** Two lines of AVG_SPEND_PER_TASK_DESCRIPTION at text-sm leading-snug. */
export const AVG_SPEND_PER_TASK_DESCRIPTION_ROW_HEIGHT_PX = 40;

/** Fixed visual block: description + team bars, bottom aligned. Same height on All and segment avg cards. */
export const AVG_SPEND_PER_TASK_VISUAL_BLOCK_HEIGHT_PX =
  AVG_SPEND_PER_TASK_DESCRIPTION_ROW_HEIGHT_PX + 12 + TEAM_COST_PER_TASK_BAR_HEIGHT_PX;

/** Left KPI stack (~40%) vs right chart area (~60%). */
export const AI_SPEND_KPI_GRID_CLASS = 'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)]';

export const SPEND_OVER_TIME_CHART_MIN_HEIGHT_CLASS = 'min-h-55';

export const SPEND_OVER_TIME_CARD_TITLE = 'AI spend over time';

export const SPEND_OVER_TIME_CARD_TOOLTIP =
  'How AI spend evolves across the selected period, as total AI spend or AI spend per task.';

export type SpendChartMode = 'total' | 'perTask';

export const SPEND_CHART_MODE_DEFAULT: SpendChartMode = 'total';

export const SPEND_CHART_MODE_OPTIONS: { value: SpendChartMode; label: string }[] = [
  { value: 'total', label: 'Total AI spend' },
  { value: 'perTask', label: 'AI spend per task' },
];

/** Matches buildAiSpendGroupAggregatesPayload peer query limit. */
export const SEGMENT_PEER_LIMIT = 100;

export const SEGMENT_TREND_ROW_LIMIT = MAX_DAILY_TREND_BUCKETS * SEGMENT_PEER_LIMIT;

export const SEGMENT_TOTAL_SPEND_TOOLTIP =
  'Total AI spend for the selected period, broken down by team, market, or tenure.';

export const SEGMENT_SPEND_PER_TASK_TOOLTIP =
  'Average AI spend per task for the selected period, broken down by team, market, or tenure.';

export const SEGMENT_SPEND_SCALE_TOOLTIP =
  "Compare each segment on the same AI spend per task scale. Dot size represents the segment's share of total AI spend.";

export const SPEND_BY_MODEL_CARD_TITLE = 'AI spend by task and model';

export const SPEND_BY_MODEL_CARD_TOOLTIP = 'See AI spend per task, broken down by LLM provider and model.';

/** Header layout when the title and model legend share one row. */
export const SPEND_BY_MODEL_HEADER_TITLE_CHIP_CLASS = 'max-w-sm';

export const SPEND_BY_MODEL_HEADER_TITLE_CLASS = 'shrink-0';

export const SPEND_BY_MODEL_HEADER_TOOLBAR_CLASS =
  'flex min-w-0 flex-1 items-center overflow-hidden';

export const SPEND_BY_MODEL_HEADER_TRAILING_CLASS = 'min-w-0 flex-1 overflow-hidden';


export const SPEND_BY_MODEL_ROW_LIMIT = 1000;

/** Every task gets a column, so the chart scrolls sideways once the columns stop fitting. */
export const SPEND_BY_MODEL_TASK_COLUMN_MIN_WIDTH_PX = 88;

export const SPEND_BY_MODEL_OTHER_LABEL = 'Other models';

export const SPEND_BY_MODEL_CHART_MIN_HEIGHT_CLASS = 'min-h-70';

export const SPEND_BY_MODEL_EMPTY_TITLE = 'No model data computed yet';
